import { FullStory, init, isInitialized } from "@fullstory/browser";
import { hasProductAccess } from "utils/productAccess";
import { getSignupAttribution } from "utils/metaPixel";
import { FULLSTORY_PAGES, resolveFullstoryPageName } from "utils/fullstoryPages";

function getFullstoryOrgId() {
  return process.env.REACT_APP_FULLSTORY_ORG_ID || "";
}

function isFullstoryEnvEnabled() {
  return process.env.REACT_APP_FULLSTORY_ENABLED === "true";
}

const EXACT_PUBLIC_MARKETING_PATHS = new Set([
  "/",
  "/pricing",
  "/show-pricing",
  "/show",
  "/for-administrators",
  "/why-lesson-launchpad",
  "/tools",
  "/classroom-timer",
  "/countdown-timer",
  "/random-student-picker",
  "/classroomscreen-alternative",
  "/metronome",
  "/tone-generator",
  "/out-of-tune",
  "/music-tools",
  "/tuner",
  "/referral-program-terms",
  "/blog",
  "/privacy",
  "/trust",
  "/whats-new",
  "/active-dates-vs-linked-boxes",
  "/auth/login",
  "/auth/register",
  "/account/subscription",
  "/what-is-classroom-automation",
]);

const PREFIX_PUBLIC_MARKETING_PATHS = [
  "/blog/",
  "/compare/",
  "/demo/",
  "/r/",
];

const BLOCKED_PATHS = new Set([
  "/classes-manager",
  "/contact",
  "/debug",
  "/auth/logout",
  "/quote",
]);

const BLOCKED_PATH_PREFIXES = [
  "/classes-manager/",
  "/share",
  "/auth/logout",
  "/spotify-callback",
  "/canva-callback",
  "/canva-return",
  "/gmail-callback",
  "/school/",
  "/purchase-order",
  "/admin",
  "/admin-panel",
];

const ATTRIBUTION_PROPERTY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_campaign_id",
  "utm_adset",
  "utm_adset_id",
  "utm_content",
  "utm_ad_id",
  "fbclid",
];

let lastIdentifiedUid = null;
let lastIdentityMode = null;
let captureActive = false;
let lastAttributionSignature = null;
let lastPageSignature = null;

export function isFullstoryConfigured() {
  return Boolean(getFullstoryOrgId() && isFullstoryEnvEnabled());
}

export function isBlockedFullstoryPath(pathname) {
  if (!pathname) {
    return true;
  }

  if (BLOCKED_PATHS.has(pathname)) {
    return true;
  }

  return BLOCKED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function isPublicMarketingPath(pathname) {
  if (isBlockedFullstoryPath(pathname)) {
    return false;
  }

  if (EXACT_PUBLIC_MARKETING_PATHS.has(pathname)) {
    return true;
  }

  if (PREFIX_PUBLIC_MARKETING_PATHS.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  // Classroom SEO tool pages under /classroom-* and similar public marketing routes.
  if (
    pathname.startsWith("/classroom-")
    || pathname.startsWith("/countdown-")
    || pathname.startsWith("/random-")
  ) {
    return true;
  }

  return false;
}

/**
 * Present / class display routes look like /:classId.
 * Marketing "/" is handled separately via isPublicMarketingPath for anonymous visitors.
 */
export function isPresentScreenPath(pathname) {
  return resolveFullstoryPageName(pathname) === FULLSTORY_PAGES.product.present;
}

/**
 * Capture marketing, reactivation, and authenticated product usage while keeping
 * admin, share, OAuth, and other explicitly blocked surfaces out of session replay.
 */
export function getRecordingPlan({ authUser, user, pathname }) {
  if (
    !isFullstoryConfigured()
    || isBlockedFullstoryPath(pathname)
    || isPresentScreenPath(pathname)
  ) {
    return null;
  }

  const isAuthenticated = Boolean(authUser?.uid && user?.uid);
  const hasAccess = isAuthenticated && hasProductAccess(user);

  // Logged-in users without product access see ProductAccessGate inside the app shell.
  // Record that reactivation experience regardless of the underlying route.
  if (isAuthenticated && !hasAccess) {
    return {
      mode: "identified",
      uid: authUser.uid,
      experience: "reactivation_gate",
    };
  }

  // Public marketing / pricing / auth — anonymous or signed-in browsing.
  if (isPublicMarketingPath(pathname)) {
    if (isAuthenticated) {
      return {
        mode: "identified",
        uid: authUser.uid,
        experience: "marketing",
      };
    }

    return {
      mode: "anonymous",
      experience: "marketing",
    };
  }

  if (hasAccess) {
    return {
      mode: "identified",
      uid: authUser.uid,
      experience: "product",
    };
  }

  return null;
}

function safeFullStoryCall(operation, payload) {
  try {
    return FullStory(operation, payload);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[FullStory]", operation, error);
    }
    return undefined;
  }
}

function stopFullstoryCapture() {
  if (isInitialized() && captureActive) {
    safeFullStoryCall("shutdown");
    captureActive = false;
  }

  lastIdentityMode = null;
  lastIdentifiedUid = null;
  lastAttributionSignature = null;
  lastPageSignature = null;
}

function ensureFullstoryCaptureActive() {
  const orgId = getFullstoryOrgId();
  if (!isInitialized()) {
    init({
      orgId,
      // Keep marketing copy readable; FullStory still masks password inputs by default.
      // Sensitive classroom routes are excluded via getRecordingPlan.
    });
    captureActive = true;
    return;
  }

  if (!captureActive) {
    safeFullStoryCall("restart");
    captureActive = true;
  }
}

function syncFullstoryIdentity(plan) {
  if (plan.mode === "anonymous") {
    if (lastIdentityMode !== "anonymous") {
      safeFullStoryCall("setIdentity", { anonymous: true });
      lastIdentityMode = "anonymous";
      lastIdentifiedUid = null;
    }
    return;
  }

  if (plan.mode === "identified" && plan.uid) {
    if (lastIdentityMode !== "identified" || lastIdentifiedUid !== plan.uid) {
      safeFullStoryCall("setIdentity", { uid: plan.uid });
      lastIdentityMode = "identified";
      lastIdentifiedUid = plan.uid;
      // Identity change clears prior anonymous property signature so we re-attach UTMs.
      lastAttributionSignature = null;
    }
  }
}

function pickTouchProperties(touch, prefix) {
  if (!touch || typeof touch !== "object") {
    return {};
  }

  const properties = {};
  ATTRIBUTION_PROPERTY_KEYS.forEach((key) => {
    const value = touch[key];
    if (value) {
      properties[`${prefix}_${key}`] = String(value);
    }
  });
  return properties;
}

export function buildFullstoryAttributionProperties(attribution = getSignupAttribution()) {
  const firstTouch = attribution?.firstTouch || null;
  const latestTouch = attribution?.latestTouch || firstTouch || null;

  if (!firstTouch && !latestTouch) {
    return null;
  }

  // Flat latest-touch aliases for the fastest FullStory filters during live campaigns.
  const latestAliases = {};
  ATTRIBUTION_PROPERTY_KEYS.forEach((key) => {
    const value = latestTouch?.[key] || firstTouch?.[key];
    if (value) {
      latestAliases[key] = String(value);
    }
  });

  return {
    ...latestAliases,
    ...pickTouchProperties(firstTouch, "first_touch"),
    ...pickTouchProperties(latestTouch, "latest_touch"),
  };
}

function syncFullstoryAttributionProperties() {
  const properties = buildFullstoryAttributionProperties();
  if (!properties || !Object.keys(properties).length) {
    return;
  }

  const signature = JSON.stringify(properties);
  if (signature === lastAttributionSignature) {
    return;
  }

  safeFullStoryCall("setProperties", {
    type: "user",
    properties,
  });
  lastAttributionSignature = signature;
}

function syncFullstoryExperience(plan, pathname) {
  const experience = plan.experience || "marketing";
  const pageName = experience === "reactivation_gate"
    ? FULLSTORY_PAGES.system.reactivationGate
    : resolveFullstoryPageName(pathname);
  const pageProperties = {
    llp_experience: experience,
  };

  if (pageName) {
    pageProperties.pageName = pageName;
  }

  const pageSignature = JSON.stringify(pageProperties);
  if (pageSignature === lastPageSignature) {
    return;
  }

  lastPageSignature = pageSignature;
  safeFullStoryCall("setProperties", {
    type: "page",
    properties: pageProperties,
  });
}

export function syncFullstorySession({ authUser, user, pathname }) {
  try {
    const plan = getRecordingPlan({ authUser, user, pathname });

    if (!plan) {
      stopFullstoryCapture();
      return;
    }

    ensureFullstoryCaptureActive();
    syncFullstoryIdentity(plan);
    syncFullstoryAttributionProperties();
    syncFullstoryExperience(plan, pathname);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[FullStory] syncFullstorySession failed", error);
    }
  }
}

export function getFullstoryReplaySessionId() {
  try {
    if (!isInitialized() || !captureActive) {
      return null;
    }
    const sessionUrl = safeFullStoryCall("getSession", { format: "url" });
    if (typeof sessionUrl === "string" && sessionUrl) {
      return sessionUrl;
    }
    const sessionId = safeFullStoryCall("getSession", { format: "id" });
    return typeof sessionId === "string" && sessionId ? sessionId : null;
  } catch (error) {
    return null;
  }
}

export function setFullstoryVisitorIntentProperties({
  intentReason,
  reminderScheduled = false,
  reminderDate = null,
} = {}) {
  try {
    if (!isInitialized() || !captureActive) {
      return;
    }
    const properties = {
      llp_intent_reason: intentReason ? String(intentReason) : undefined,
      llp_reminder_scheduled: Boolean(reminderScheduled),
    };
    if (reminderDate) {
      properties.llp_reminder_date = String(reminderDate);
    }
    safeFullStoryCall("setProperties", {
      type: "user",
      properties,
    });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[FullStory] setFullstoryVisitorIntentProperties failed", error);
    }
  }
}

export function resetFullstorySessionStateForTests() {
  lastIdentifiedUid = null;
  lastIdentityMode = null;
  captureActive = false;
  lastAttributionSignature = null;
  lastPageSignature = null;
}
